import classNames from 'classnames';
import { Annotation, Editor, EditorConfiguration } from 'codemirror';
import 'codemirror/addon/display/placeholder';
import 'codemirror/addon/lint/lint';
import 'codemirror/mode/javascript/javascript';
import _ from 'lodash';
import React from 'react';
import { UnControlled as CodeMirror } from 'react-codemirror2';

import { MaybeUndef } from '../../types/system';

import styles from './json-editor.scss';
import {
  validateJsonSchema,
  validateJsonStructure,
} from './json-validation.utlis';

export const JSON_EDITOR_GUTTERS = [
  'CodeMirror-linenumbers',
  'CodeMirror-lint-markers',
];

export interface JsonEditorProps {
  json?: string;
  readOnly?: boolean;
  className?: string;
  onJsonChange?(value: string): void;
  onChange?(value: string): void;
  error?: boolean;
  onInstanceReceive?(codeMirrorInstance: Editor): void;
  onBlur?(editor: Editor, event: Event): void;
  onFocus?(event: React.FocusEvent<HTMLDivElement>): void;
  jsonSchema?: object;
  placeholder?: string;
}

interface DefaultProps {
  readOnly: boolean;
  onInstanceReceive(): void;
  onChange(value: string): void;
}

interface State {
  optionsCached: EditorConfiguration;
}

export class JsonEditor extends React.PureComponent<JsonEditorProps, State> {
  public static defaultProps: DefaultProps = {
    readOnly: true,
    onInstanceReceive: _.noop,
    onChange: _.noop,
  };

  private codeMirrorInstance: Editor;
  public actualCodeMirrorJson: MaybeUndef<string>;
  public state: State;

  constructor(props) {
    super(props);

    this.actualCodeMirrorJson = props.json;
    // cache options to avoid unwanted codemirror render cycles
    this.state = {
      optionsCached: this.getOptions(props),
    };
  }

  private getOptions({
    placeholder,
    readOnly,
  }: JsonEditorProps): EditorConfiguration {
    return {
      readOnly,
      mode: 'application/json',
      placeholder,
      lineNumbers: true,
      lint: {
        async: false,
        hasGutters: true,
        getAnnotations: this.lintJsonText.bind(this),
      },
      gutters: JSON_EDITOR_GUTTERS,
      theme: 'wix-mp-json',
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps: JsonEditorProps) {
    const newJson = nextProps.json || '';

    // Force update codemirror value when json was changed outside
    if (this.props.json !== newJson && newJson !== this.actualCodeMirrorJson) {
      this.actualCodeMirrorJson = newJson;
      if (this.codeMirrorInstance) {
        this.codeMirrorInstance.setValue(newJson);
      }
    }

    // refresh codemirror options cashe when needed
    if (
      this.props.readOnly !== nextProps.readOnly ||
      this.props.jsonSchema !== nextProps.jsonSchema
    ) {
      this.setState({
        optionsCached: this.getOptions(nextProps),
      });
    }
  }

  private onEditorDidMount(editor: Editor) {
    const { onInstanceReceive = _.noop } = this.props;

    this.codeMirrorInstance = editor;
    onInstanceReceive(editor);
  }

  public handleChange(editor, data, value) {
    const { onChange } = this.props as JsonEditorProps & DefaultProps;

    this.actualCodeMirrorJson = value;

    onChange(value);
  }

  public getJsonValue(): MaybeUndef<string> {
    return this.actualCodeMirrorJson;
  }

  private lintJsonText(content: string): Annotation[] {
    if (_.isEmpty(_.trim(content))) {
      return [];
    }

    const { jsonSchema } = this.props;
    const structureErrors = validateJsonStructure(content);

    if (structureErrors.length > 0) {
      return structureErrors;
    }

    return jsonSchema ? validateJsonSchema(content, jsonSchema) : [];
  }

  public render() {
    const { json, className, error, onFocus } = this.props;

    return (
      <div
        className={classNames(className, { [styles.error]: error })}
        onFocus={onFocus}
        data-hook="json-editor"
      >
        <CodeMirror
          editorDidMount={this.onEditorDidMount.bind(this)}
          options={this.state.optionsCached}
          className={styles.codeMirror}
          value={json}
          onChange={this.handleChange.bind(this)}
        />
      </div>
    );
  }
}
